import React, { Component, ReactNode } from 'react';
import cx from 'classnames';
import CSSTransition, { CSSTransitionProps } from 'react-transition-group/CSSTransition';

import { LinkV2 } from '../LinkV2';
import CheckmarkCircleIcon, { CheckmarkCircleIconProps } from '../SvgIcons/CheckmarkCircle';
import { AlertIcon } from '../SvgIcons/Alert';
import { MailIcon } from '../SvgIcons/Mail';
import ToastActionTypes from './types/ToastActionTypes';

import './Toast.less';

type ToastProps = Omit<CSSTransitionProps, 'classNames' | 'timeout'> & {
  toast: {
    id: number;
    type: keyof typeof ToastActionTypes;
    headline: string | ReactNode;
    dek?: string | ReactNode;
    className?: string;
    autoDismissInSeconds: number;
    dismissText?: string | ReactNode;
    onDismiss?: () => void;
    linkText?: string | ReactNode;
    linkDestination?: string;
    onLinkClick?: () => void;
  };
  onRemove: (id: number, autoDismissMs?: number) => void;
};

export default class Toast extends Component<ToastProps> {
  constructor(props: ToastProps) {
    super(props);
    this.handleDismiss = this.handleDismiss.bind(this);
  }

  componentDidMount(): void {
    const {
      onRemove,
      toast: { id, autoDismissInSeconds },
    } = this.props;
    if (autoDismissInSeconds !== 0) onRemove(id, autoDismissInSeconds * 1000);
  }

  getIcon(type: keyof typeof ToastActionTypes): React.FC<CheckmarkCircleIconProps> {
    switch (type) {
      case ToastActionTypes.NEGATIVE:
        return AlertIcon;
      case ToastActionTypes.MESSAGE:
        return MailIcon;
      case ToastActionTypes.POSITIVE:
      default:
        return CheckmarkCircleIcon;
    }
  }

  handleDismiss(): void {
    const {
      onRemove,
      toast: { id, onDismiss },
    } = this.props;
    // User propogated side effect func
    if (onDismiss) onDismiss();
    // Default toast self-removal func
    onRemove(id);
  }

  render(): JSX.Element {
    const {
      toast: {
        id,
        type,
        headline,
        dek,
        className,
        dismissText,
        linkText,
        linkDestination,
        onLinkClick,
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onRemove,
      ...rest
    } = this.props;

    // Used by CSSTransition
    const modifiedRest = Object.assign({}, rest);

    const IconComponent = this.getIcon(type);
    const classes = cx('zui-toast', `zui-toast--${type.toLowerCase()}`, className);

    return (
      <CSSTransition classNames="zui-toast" {...modifiedRest} timeout={600}>
        <div id={`toast-${id}`} className={classes}>
          <IconComponent
            className="zui-toast__icon"
            width={24}
            height={24}
            title={`${type.toLowerCase()}-icon`}
          />
          <div className="zui-toast__content">
            <div className="zui-toast__headline">{headline}</div>
            {dek && <div className="zui-toast__dek">{dek}</div>}
          </div>
          {dismissText && (
            <LinkV2
              variant="tertiary"
              role="button"
              className="zui-toast__dismiss"
              onClick={this.handleDismiss}
            >
              {dismissText}
            </LinkV2>
          )}
          {linkText && linkDestination && (
            <LinkV2
              variant="tertiary"
              role="link"
              arrow
              className="zui-toast__link"
              href={linkDestination}
              onClick={onLinkClick}
            >
              {linkText}
            </LinkV2>
          )}
        </div>
      </CSSTransition>
    );
  }
}
